import { parsePoint } from "@commerce/orders/meta";

/*
 * Small, exact-enough geometry for delivery areas: a city's worth of zones on a sphere.
 * Positions are GeoJSON [lng, lat]. Nothing here decides who is delivered to; the server's
 * `resolveBranch` does. These only draw and warn.
 */

export type Position = [number, number];
export type Ring = Position[];

export interface PolygonGeometry {
	type: "Polygon";
	coordinates: Ring[];
}
export interface MultiPolygonGeometry {
	type: "MultiPolygon";
	coordinates: Ring[][];
}
export type ZoneGeometry = PolygonGeometry | MultiPolygonGeometry;

const EARTH_RADIUS_M = 6_371_008.8;
const rad = (deg: number) => (deg * Math.PI) / 180;
const deg = (r: number) => (r * 180) / Math.PI;

/** Reads a stored zone; anything that is not a Polygon or MultiPolygon of numbers is null. */
export function asZone(value: unknown): ZoneGeometry | null {
	if (!value || typeof value !== "object") {
		return null;
	}
	const zone = value as { type?: unknown; coordinates?: unknown };
	if (zone.type === "Polygon" && Array.isArray(zone.coordinates)) {
		return zone as PolygonGeometry;
	}
	if (zone.type === "MultiPolygon" && Array.isArray(zone.coordinates)) {
		return zone as MultiPolygonGeometry;
	}
	return null;
}

/** The outer rings of a zone, one per polygon. */
export function polygonsOf(zone: ZoneGeometry): Ring[][] {
	return zone.type === "Polygon" ? [zone.coordinates] : zone.coordinates;
}

/** A circle as a polygon of `steps` points, `radiusM` around `center`. */
export function circlePolygon(
	center: Position,
	radiusM: number,
	steps = 64,
): PolygonGeometry {
	const [lng, lat] = center;
	const angular = radiusM / EARTH_RADIUS_M;
	const ring: Ring = [];
	for (let i = 0; i <= steps; i++) {
		const bearing = (2 * Math.PI * (i % steps)) / steps;
		const lat2 = Math.asin(
			Math.sin(rad(lat)) * Math.cos(angular) +
				Math.cos(rad(lat)) * Math.sin(angular) * Math.cos(bearing),
		);
		const lng2 =
			rad(lng) +
			Math.atan2(
				Math.sin(bearing) * Math.sin(angular) * Math.cos(rad(lat)),
				Math.cos(angular) - Math.sin(rad(lat)) * Math.sin(lat2),
			);
		ring.push([deg(lng2), deg(lat2)]);
	}
	return { type: "Polygon", coordinates: [ring] };
}

/** Spherical area of a ring in square metres (Chamberlain and Duquette). */
function ringArea(ring: Ring): number {
	let total = 0;
	for (let i = 0; i < ring.length - 1; i++) {
		const [lng1, lat1] = ring[i] as Position;
		const [lng2, lat2] = ring[i + 1] as Position;
		total +=
			rad(lng2 - lng1) * (2 + Math.sin(rad(lat1)) + Math.sin(rad(lat2)));
	}
	return Math.abs((total * EARTH_RADIUS_M * EARTH_RADIUS_M) / 2);
}

export function zoneAreaM2(zone: ZoneGeometry): number {
	return polygonsOf(zone).reduce((sum, rings) => {
		const [outer, ...holes] = rings;
		return (
			sum +
			(outer ? ringArea(outer) : 0) -
			holes.reduce((h, ring) => h + ringArea(ring), 0)
		);
	}, 0);
}

/** "14 km²", "0.6 km²". */
export function areaLabel(m2: number): string {
	const km2 = m2 / 1_000_000;
	return `${km2 >= 10 ? Math.round(km2).toLocaleString("en-US") : km2.toFixed(1)} km²`;
}

/** "5 km", "800 m". */
export function distanceLabel(m: number): string {
	return m >= 1000
		? `${(m / 1000).toFixed(m >= 10_000 ? 0 : 1).replace(/\.0$/, "")} km`
		: `${Math.round(m)} m`;
}

export function haversineM(a: Position, b: Position): number {
	const dLat = rad(b[1] - a[1]);
	const dLng = rad(b[0] - a[0]);
	const h =
		Math.sin(dLat / 2) ** 2 +
		Math.cos(rad(a[1])) * Math.cos(rad(b[1])) * Math.sin(dLng / 2) ** 2;
	return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
}

/** Ray casting on lng/lat: fine at city scale, away from the poles and the antimeridian. */
export function pointInRing(point: Position, ring: Ring): boolean {
	let inside = false;
	const [x, y] = point;
	for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
		const [xi, yi] = ring[i] as Position;
		const [xj, yj] = ring[j] as Position;
		if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
			inside = !inside;
		}
	}
	return inside;
}

export function pointInZone(point: Position, zone: ZoneGeometry): boolean {
	return polygonsOf(zone).some(([outer, ...holes]) =>
		outer
			? pointInRing(point, outer) &&
				!holes.some((hole) => pointInRing(point, hole))
			: false,
	);
}

function segmentsCross(a: Position, b: Position, c: Position, d: Position) {
	const orient = (p: Position, q: Position, r: Position) =>
		Math.sign(
			(q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]),
		);
	return (
		orient(a, b, c) !== orient(a, b, d) &&
		orient(c, d, a) !== orient(c, d, b)
	);
}

function ringsTouch(a: Ring, b: Ring): boolean {
	if (a.some((p) => pointInRing(p, b)) || b.some((p) => pointInRing(p, a))) {
		return true;
	}
	for (let i = 0; i < a.length - 1; i++) {
		for (let j = 0; j < b.length - 1; j++) {
			if (
				segmentsCross(
					a[i] as Position,
					a[i + 1] as Position,
					b[j] as Position,
					b[j + 1] as Position,
				)
			) {
				return true;
			}
		}
	}
	return false;
}

/** Whether two delivery areas share any ground (outer rings only). */
export function zonesOverlap(a: ZoneGeometry, b: ZoneGeometry): boolean {
	const outersA = polygonsOf(a).flatMap(([outer]) => (outer ? [outer] : []));
	const outersB = polygonsOf(b).flatMap(([outer]) => (outer ? [outer] : []));
	return outersA.some((ra) => outersB.some((rb) => ringsTouch(ra, rb)));
}

/** The area a branch delivers to as one shape: its drawn zone, else its radius circle. */
export function coverageOf(store: {
	deliveryZone: unknown;
	deliveryRadiusM: number | null;
	latitude: number | null;
	longitude: number | null;
}): ZoneGeometry | null {
	const zone = asZone(store.deliveryZone);
	if (zone) {
		return zone;
	}
	if (
		store.deliveryRadiusM &&
		store.latitude !== null &&
		store.longitude !== null
	) {
		return circlePolygon(
			[store.longitude, store.latitude],
			store.deliveryRadiusM,
		);
	}
	return null;
}

/** Pairs of branch ids whose areas overlap, for the "overlaps" warning. */
export function overlappingPairs(
	stores: ReadonlyArray<{ id: string; coverage: ZoneGeometry | null }>,
): Array<[string, string]> {
	const pairs: Array<[string, string]> = [];
	for (let i = 0; i < stores.length; i++) {
		for (let j = i + 1; j < stores.length; j++) {
			const a = stores[i];
			const b = stores[j];
			if (
				a?.coverage &&
				b?.coverage &&
				zonesOverlap(a.coverage, b.coverage)
			) {
				pairs.push([a.id, b.id]);
			}
		}
	}
	return pairs;
}

/** [[west, south], [east, north]] of everything given, or null. */
export function boundsOf(
	shapes: ReadonlyArray<ZoneGeometry | Position | null>,
): [Position, Position] | null {
	let west = Number.POSITIVE_INFINITY;
	let south = Number.POSITIVE_INFINITY;
	let east = Number.NEGATIVE_INFINITY;
	let north = Number.NEGATIVE_INFINITY;
	const add = ([lng, lat]: Position) => {
		west = Math.min(west, lng);
		east = Math.max(east, lng);
		south = Math.min(south, lat);
		north = Math.max(north, lat);
	};
	for (const shape of shapes) {
		if (!shape) {
			continue;
		}
		if (Array.isArray(shape)) {
			add(shape);
		} else {
			for (const rings of polygonsOf(shape)) {
				for (const p of rings[0] ?? []) {
					add(p);
				}
			}
		}
	}
	return Number.isFinite(west)
		? [
				[west, south],
				[east, north],
			]
		: null;
}

/** A zone's outer ring as "lat, lng" lines, the way people paste points from a map. */
export function ringToText(zone: ZoneGeometry): string {
	const outer = polygonsOf(zone)[0]?.[0] ?? [];
	return outer
		.slice(0, -1)
		.map(([lng, lat]) => `${lat.toFixed(6)}, ${lng.toFixed(6)}`)
		.join("\n");
}

/**
 * A polygon from "lat, lng" lines (the list-based way to edit a zone): at least three
 * points, closed here. Null with the line that is wrong otherwise.
 */
export function ringFromText(
	text: string,
): { zone: PolygonGeometry } | { error: string } {
	const ring: Ring = [];
	const lines = text
		.split(/\n+/)
		.map((line) => line.trim())
		.filter(Boolean);
	for (const [index, line] of lines.entries()) {
		const match =
			/^(-?\d{1,2}(?:\.\d+)?)\s*[,\s]\s*(-?\d{1,3}(?:\.\d+)?)$/.exec(
				line,
			);
		const lat = Number(match?.[1]);
		const lng = Number(match?.[2]);
		if (!match || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
			return { error: `Line ${index + 1} is not "latitude, longitude"` };
		}
		ring.push([lng, lat]);
	}
	if (ring.length < 3) {
		return { error: "A zone needs at least three points" };
	}
	const first = ring[0] as Position;
	const last = ring[ring.length - 1] as Position;
	if (first[0] !== last[0] || first[1] !== last[1]) {
		ring.push([first[0], first[1]]);
	}
	return { zone: { type: "Polygon", coordinates: [ring] } };
}

/**
 * The centre and radius of a circle terra-draw drew. Its `radiusKilometers` property keeps
 * the starting radius, not the dragged one, so both are read from the ring itself.
 */
export function circleOf(
	geometry: PolygonGeometry,
): { center: Position; radiusM: number } | null {
	const points = (geometry.coordinates[0] ?? []).slice(0, -1);
	if (points.length < 3) {
		return null;
	}
	const center: Position = [
		points.reduce((s, p) => s + p[0], 0) / points.length,
		points.reduce((s, p) => s + p[1], 0) / points.length,
	];
	const radiusM =
		points.reduce((s, p) => s + haversineM(center, p), 0) / points.length;
	return radiusM > 0 ? { center, radiusM: Math.round(radiusM) } : null;
}

/** A pin ("25.2, 55.3"), a Google Maps link, or an area name, as checkLocation takes it. */
export function locationQuery(
	text: string,
):
	| { lat: number; lng: number }
	| { mapsUrl: string }
	| { area: string }
	| null {
	const trimmed = text.trim();
	if (trimmed.length < 2) {
		return null;
	}
	const point = parsePoint(trimmed);
	if (point) {
		return { lat: point.latitude, lng: point.longitude };
	}
	if (/^https?:\/\//i.test(trimmed)) {
		return { mapsUrl: trimmed };
	}
	return { area: trimmed.slice(0, 200) };
}
